/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState, useEffect } from "react";
import { connect } from "react-redux";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import { Popover, Chip } from "@material-ui/core";
import { List, PlayCircleFilled, Share, CheckCircle } from "@material-ui/icons";

import Button from "components/CustomButtons/Button";

import {
  sLogo,
  sGoToCourse,
  sEnroll,
  sShare,
} from "assets/jss/views/ResStrings";
import SocialShare from "client/views/LandingPage/SocialShare";
import { EnrollDialog } from "./EnrollDialog";

import {
  getCurrentCourse,
  getCurrentLecture,
  ApricotAction,
} from "client/actions/courses";
import {
  selectorCurrentCourse,
  selectorCurrentLecture,
} from "client/selectors/courseSelector";
import { AllOwns } from "client/reducers/ownReducer";

import coursePageStyle from "assets/jss/views/coursePage/coursePageStyle.jsx";
const styles = createStyles(coursePageStyle as any);

export type CourseActionParams = {
  tooltip: string;
  text: string;
  icon: React.ReactElement;
  color: "primary" | "secondary" | "warning";
  action: (e?: any) => void;
  useChip?: boolean;
  classes?: any;
  hidden?: boolean;
};

export type ActionButtonInfo = {
  cid: string;
  lid: string | undefined;
  owned: boolean;
  vPos: number;
  action: "resume" | "start" | undefined;
  sched: boolean; // Scheduled Only Course
};

interface Props extends WithStyles<typeof styles> {
  getCurrentCourse: ApricotAction;
  getCurrentLecture: ApricotAction;
  // store data
  user: any;
  currentCourse: any;
  currentLecture: any;
  classes: any;
  history: any;
  // compnent props
  inCard?: boolean; // used in a course Card (CourseListPage)
  inStudy?: boolean; // used in MyStudy
  info: ActionButtonInfo;
}

const CourseActionsBase: React.FC<Props> = ({
  getCurrentCourse,
  getCurrentLecture,
  user,
  currentCourse,
  currentLecture,
  classes,
  history,
  inCard,
  inStudy,
  info,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const [showEnroll, setShowEnroll] = useState(false);
  const [classroomURL, setClassroomURL] = useState("");

  useEffect(() => {
    if (
      classroomURL !== "" &&
      currentCourse &&
      currentCourse._id === info.cid &&
      currentLecture &&
      currentLecture._id === info.lid
    ) {
      history.push({
        pathname: classroomURL,
        state: { autoPlay: true },
      });
    }
  }, [classroomURL, currentCourse, currentLecture]);

  const handleShare = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    open: Boolean,
  ) => {
    event.preventDefault();
    setAnchorEl(open ? event.currentTarget : null);
  };

  const renderShareButton = () =>
    !inStudy &&
    courseActionBtn({
      tooltip: "Share this course",
      text: "Share",
      color: "warning",
      action: (e) => handleShare(e, true),
      icon: <Share />,
      useChip: inCard,
      classes,
      hidden: info.sched,
    });

  const enrollButton = () => (
    <div className={classes.actionButton}>
      {courseActionBtn({
        tooltip: "Enroll this course",
        text: sEnroll,
        color: "primary",
        action: () => setShowEnroll(true),
        icon: <CheckCircle />,
        useChip: inCard,
        hidden: info.sched,
      })}
    </div>
  );

  const renderOwnedButton = () => {
    if (!info.action) {
      return <></>;
    }

    const buttonLabels = {
      resume: "Resume Video",
      start: "Start the First Video ",
    };

    const goClassroom = () => {
      getCurrentCourse(info.cid);
      getCurrentLecture(info.lid);
      setClassroomURL(`/clsrm/${info.cid}/${info.lid}`);
    };

    const text = buttonLabels[info.action];
    return (
      <div
        className={inStudy ? classes.courseActionBase : classes.actionButton}>
        {courseActionBtn({
          tooltip: text,
          text,
          color: "secondary",
          action: goClassroom,
          icon: <PlayCircleFilled />,
          useChip: !inStudy && inCard,
        })}
      </div>
    );
  };

  const openButton = () => {
    var sc; // style class
    if (inStudy) {
      sc = classes.courseActionBase;
    } else {
      // style 'openButton' will hide this button when w < 600
      // If the course is owned, and there's no action (resume...), always show the Open button,
      // (use style 'actionButton') since there's no "Add to Cart", we got space.
      sc =
        info.owned && !info.action ? classes.actionButton : classes.openButton;
    }
    return (
      (inCard || (inStudy && !info.action)) && (
        <div className={sc}>
          {courseActionBtn({
            tooltip: sGoToCourse,
            icon: <List />,
            text: "Open",
            color: "secondary",
            action: () => history.push(`/crp/${info.cid}`),
            useChip: !inStudy && inCard,
          })}
        </div>
      )
    );
  };

  return (
    <>
      {showEnroll && (
        <EnrollDialog
          owned={false}
          enroll={true}
          courses={[info.cid]}
          onExit={() => setShowEnroll(false)}
        />
      )}

      <div className={classes.courseActionBase}>
        {openButton()}
        {info.owned ? renderOwnedButton() : enrollButton()}
        {renderShareButton()}
        {renderSharePopover(
          `https://apricad.com/crp/${info.cid}`,
          anchorEl,
          (e) => handleShare(e, false),
        )}
      </div>
    </>
  );
};

function mapStateToProps(state: any) {
  return {
    user: state.state_auth,
    currentCourse: selectorCurrentCourse(state),
    currentLecture: selectorCurrentLecture(state),
    // cart: selectorCart(state),
  };
}

const base: any = CourseActionsBase;
export const CourseActions: any = withStyles(styles)(
  connect(mapStateToProps, {
    getCurrentCourse,
    getCurrentLecture,
  })(base),
);

export const getCourseActionProps = (
  owns: AllOwns,
  course: any,
  showStart: boolean,
) => {
  var info: ActionButtonInfo = {
    cid: course._id,
    lid: undefined,
    action: undefined,
    owned: false,
    vPos: 0,
    sched: course.sched,
  };

  if (owns[course._id]) {
    info.owned = true;
    if (owns[course._id].currentLec) {
      // if the course has been started, show "RESUME"
      info.lid = owns[course._id].currentLec;
      info.vPos = owns[course._id].currentVPos;
      info.action = "resume";
    } else {
      if (showStart && course.lectures[0]) {
        info.lid = course.lectures[0]._id;
        info.action = "start";
      }
    }
  }

  // const price = {
  //   curPrice: course.curPrice,
  //   regPrice: course.regPrice,
  // };

  return { info };
};

export const courseActionBtn = (params: CourseActionParams) => {
  const {
    icon,
    text,
    color,
    action,
    useChip,
    classes,
    hidden = false,
  } = params;
  if (hidden) return "";

  return useChip ? (
    <Chip
      classes={
        classes ? { root: classes.shareColor, icon: classes.shareColor } : {}
      }
      icon={icon}
      label={text}
      color={classes ? undefined : color}
      onClick={(e: any) => action(e)}
    />
  ) : (
    <>
      <Button
        round
        color={color}
        onClick={(e: any) => action(e)}
        style={{ color: "white" }}>
        {icon}
        {text}
      </Button>
    </>
  );
};

export const renderSharePopover = (
  link: string,
  anchorEl: any,
  onClose: (e: any) => void,
) => {
  const openShare = Boolean(anchorEl);
  const id = openShare ? "share-popover" : undefined;

  return (
    <Popover
      id={id}
      open={openShare}
      anchorEl={anchorEl}
      onClose={(e: any) => onClose(e)}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}>
      <SocialShare shareUrl={link} title={`Apricad, ${sLogo}`} body={sShare} />
    </Popover>
  );
};
